import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../../api/apiServices";

// ── Alerts Tab — shows AdminAlert records (transaction errors) ──
function AlertsTab() {
  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [filter,  setFilter]  = useState("all"); // all | unread | critical
  const LIMIT = 50;

  const SEVERITY_COLORS = {
    critical: { text: "text-red-400",    bg: "bg-red-500/10",    dot: "#f87171" },
    high:     { text: "text-orange-400", bg: "bg-orange-500/10", dot: "#fb923c" },
    medium:   { text: "text-amber-400",  bg: "bg-amber-500/10",  dot: "#fbbf24" },
    low:      { text: "text-blue-400",   bg: "bg-blue-500/10",   dot: "#60a5fa" },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filter === "unread")   params.set("read", "false");
      if (filter === "critical") params.set("severity", "critical");
      const res = await apiClient.get(`/mtrade/admin/alerts?${params}`);
      if (res.data.status) {
        setAlerts(res.data.data || []);
        setTotal(res.data.pagination?.total || res.data.data?.length || 0);
      }
    } catch {}
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await apiClient.patch(`/mtrade/admin/alerts/${id}/read`);
      setAlerts(prev => prev.map(a => a._id === id ? { ...a, read: true } : a));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await apiClient.patch("/mtrade/admin/alerts/dismiss-all");
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    } catch {}
  };

  const unreadCount = alerts.filter(a => !a.read).length;
  const totalPages  = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Transaction Alerts</h2>
          <p className="text-white/40 text-xs mt-0.5">
            Errors from deposit, withdrawal, billing and crypto operations
            {unreadCount > 0 && <span className="ml-2 text-red-400">· {unreadCount} unread</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#161616] border border-white/8 rounded-xl text-xs text-white/40 hover:text-white/60">
              <i className="ri-check-double-line" /> Mark all read
            </button>
          )}
          <button onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#161616] border border-white/8 rounded-xl text-xs text-white/40 hover:text-white/60">
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-white/5 w-fit">
        {[
          { id: "all",      label: "All" },
          { id: "unread",   label: "Unread" },
          { id: "critical", label: "Critical" },
        ].map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              filter === f.id ? "bg-white/10 text-white" : "text-white/30 hover:text-white/50"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-start gap-4 px-4 py-4">
                <div className="w-8 h-8 bg-white/5 rounded-xl animate-pulse flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="w-48 h-3 bg-white/5 rounded animate-pulse" />
                  <div className="w-full h-3 bg-white/5 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <i className="ri-checkbox-circle-line text-[#00C896]/30 text-4xl mb-2" />
            <p className="text-white/25 text-sm">No alerts</p>
            <p className="text-white/15 text-xs mt-1">Transaction errors will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-white/4">
            {alerts.map(alert => {
              const sc = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.medium;
              return (
                <div key={alert._id}
                  className={`flex items-start gap-4 px-4 py-4 transition-colors ${!alert.read ? "bg-white/[0.02]" : ""}`}>
                  {/* Severity dot */}
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${sc.bg}`}>
                    <i className="ri-error-warning-line text-sm" style={{ color: sc.dot }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white/80 text-sm font-semibold">
                          {alert.type?.replace(/_/g, " ") || "System Alert"}
                        </p>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sc.text} ${sc.bg}`}>
                          {alert.severity || "medium"}
                        </span>
                        {!alert.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-white/25 text-[10px] flex-shrink-0">
                        {fmtTime(alert.createdAt)}
                      </p>
                    </div>
                    <p className="text-white/50 text-xs leading-relaxed mb-2">{alert.message}</p>
                    {alert.userId && (
                      <p className="text-white/25 text-[10px] font-mono">User: {alert.userId}</p>
                    )}
                    {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                      <div className="mt-2 bg-[#111] border border-white/5 rounded-xl p-2.5 overflow-x-auto">
                        <pre className="text-white/30 text-[10px] font-mono leading-relaxed">
                          {JSON.stringify(alert.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>

                  {!alert.read && (
                    <button onClick={() => markRead(alert._id)}
                      className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/25 hover:text-white/60 flex-shrink-0">
                      <i className="ri-check-line text-sm" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <p className="text-white/25 text-xs">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/40 disabled:opacity-25">
                <i className="ri-arrow-left-s-line text-sm" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/40 disabled:opacity-25">
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-start gap-3 px-4 py-3 bg-[#161616] border border-white/5 rounded-xl">
        <i className="ri-information-line text-white/20 mt-0.5 flex-shrink-0" />
        <p className="text-white/30 text-xs leading-relaxed">
          These alerts are generated by deposit verifications, airtime purchases, withdrawals,
          crypto operations, and webhook processing failures. They are written by
          <code className="mx-1 px-1 bg-white/5 rounded text-[10px]">alertSystemError()</code>
          in each controller and are separate from the HTTP request audit log.
        </p>
      </div>
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────
const METHODS  = ["ALL", "GET", "POST", "PUT", "PATCH", "DELETE"];
const STATUS_RANGES = [
  { label: "All",    min: 0,   max: 999 },
  { label: "2xx",    min: 200, max: 299 },
  { label: "4xx",    min: 400, max: 499 },
  { label: "5xx",    min: 500, max: 999 },
  { label: "Errors", min: 400, max: 999 },
];

// ── Helpers ───────────────────────────────────────────────────
function statusColor(code) {
  if (!code) return { text: "text-white/20", bg: "bg-white/5",        dot: "#ffffff30" };
  if (code < 300) return { text: "text-[#00C896]", bg: "bg-[#00C896]/10", dot: "#00C896"  };
  if (code < 400) return { text: "text-blue-400",  bg: "bg-blue-500/10",  dot: "#60a5fa"  };
  if (code < 500) return { text: "text-amber-400", bg: "bg-amber-500/10", dot: "#fbbf24"  };
  return             { text: "text-red-400",   bg: "bg-red-500/10",    dot: "#f87171"  };
}

function methodColor(method) {
  const map = {
    GET:    "text-blue-400 bg-blue-500/10",
    POST:   "text-[#00C896] bg-[#00C896]/10",
    PUT:    "text-amber-400 bg-amber-500/10",
    PATCH:  "text-purple-400 bg-purple-500/10",
    DELETE: "text-red-400 bg-red-500/10",
  };
  return map[method] || "text-white/40 bg-white/5";
}

function fmtDuration(ms) {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtTime(d) {
  if (!d) return "—";
  const date = new Date(d);
  const now  = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)  return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24)  return `${diffHrs}h ago`;
  return date.toLocaleString("en-NG", {
    day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtPath(path) {
  // Truncate long paths with ellipsis in the middle
  if (!path) return "—";
  if (path.length <= 52) return path;
  return path.slice(0, 28) + "…" + path.slice(-20);
}

// ── Summary stat card ─────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = "#00C896" }) {
  return (
    <div className="bg-[#161616] border border-white/5 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: color + "18" }}>
          <i className={`${icon} text-sm`} style={{ color }} />
        </div>
        <p className="text-white/40 text-xs">{label}</p>
      </div>
      <p className="text-white font-bold text-xl">{value ?? "—"}</p>
      {sub && <p className="text-white/25 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Log row ───────────────────────────────────────────────────
function LogRow({ log, expanded, onToggle }) {
  const sc   = statusColor(log.statusCode);
  const mc   = methodColor(log.method);
  const hasDetail = log.body || log.error;

  return (
    <>
      <div
        onClick={() => hasDetail && onToggle(log._id)}
        className={`grid items-center gap-3 px-4 py-3 border-b border-white/4 last:border-0 transition-colors
          ${hasDetail ? "cursor-pointer hover:bg-white/3" : ""}
          ${expanded ? "bg-white/4" : ""}
          ${log.error ? "border-l-2 border-l-red-500/40" : ""}`}
        style={{ gridTemplateColumns: "6.5rem 3.5rem 1fr 4.5rem 3.5rem 5.5rem 1.5rem" }}
      >
        {/* Time */}
        <p className="text-white/30 text-[11px] font-mono truncate">{fmtTime(log.createdAt)}</p>

        {/* Method */}
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded font-mono text-center ${mc}`}>
          {log.method || "—"}
        </span>

        {/* Path */}
        <p className="text-white/70 text-xs font-mono truncate" title={log.path}>
          {fmtPath(log.path)}
        </p>

        {/* Status */}
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded font-mono text-center ${sc.text} ${sc.bg}`}>
          {log.statusCode || "—"}
        </span>

        {/* Duration */}
        <p className={`text-[11px] font-mono text-right ${log.durationMs > 3000 ? "text-amber-400" : "text-white/25"}`}>
          {fmtDuration(log.durationMs)}
        </p>

        {/* IP */}
        <p className="text-white/20 text-[10px] font-mono truncate text-right" title={log.ip}>
          {log.ip || "—"}
        </p>

        {/* Expand indicator */}
        <div className="flex items-center justify-center">
          {hasDetail
            ? <i className={`text-white/20 text-xs transition-transform ${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
            : <div className="w-1 h-1 rounded-full bg-white/10" />
          }
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && hasDetail && (
        <div className="bg-[#111] border-b border-white/4 px-4 py-4 space-y-3">
          {/* User info */}
          <div className="flex items-center gap-4 flex-wrap">
            {log.userId && (
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line text-white/30 text-xs" />
                <p className="text-white/50 text-xs font-mono">{log.userId}</p>
              </div>
            )}
            {log.role && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/30 font-semibold">
                {log.role}
              </span>
            )}
            {log.userAgent && (
              <p className="text-white/20 text-[10px] truncate max-w-xs" title={log.userAgent}>
                {log.userAgent}
              </p>
            )}
          </div>

          {/* Full path */}
          {log.path && log.path.length > 52 && (
            <div>
              <p className="text-white/20 text-[10px] uppercase tracking-widest mb-1">Full Path</p>
              <p className="text-white/50 text-xs font-mono break-all">{log.path}</p>
            </div>
          )}

          {/* Error */}
          {log.error && (
            <div className="bg-red-500/8 border border-red-500/15 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-error-warning-line text-red-400 text-xs" />
                <p className="text-red-400 text-[10px] font-bold uppercase tracking-wider">Error</p>
              </div>
              <p className="text-red-300/80 text-xs font-mono break-all leading-relaxed">{log.error}</p>
            </div>
          )}

          {/* Request body */}
          {log.body && Object.keys(log.body).length > 0 && (
            <div>
              <p className="text-white/20 text-[10px] uppercase tracking-widest mb-1.5">Request Body</p>
              <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-3 overflow-x-auto">
                <pre className="text-white/50 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-all">
                  {JSON.stringify(log.body, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Copy button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(JSON.stringify(log, null, 2));
            }}
            className="flex items-center gap-1.5 text-[10px] text-white/25 hover:text-white/50 transition-colors"
          >
            <i className="ri-file-copy-line" /> Copy full entry
          </button>
        </div>
      )}
    </>
  );
}

// ── Main view ─────────────────────────────────────────────────
export default function AuditLogsView() {
  const [activeTab,  setActiveTab]  = useState("requests");
  const [logs,       setLogs]       = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [expanded,   setExpanded]   = useState(null);

  // Filters
  const [userId,     setUserId]     = useState("");
  const [pathQ,      setPathQ]      = useState("");
  const [method,     setMethod]     = useState("ALL");
  const [statusRange, setStatusRange] = useState(0); // index into STATUS_RANGES
  const [errorOnly,  setErrorOnly]  = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const LIMIT = 50;
  const intervalRef = useRef(null);
  const userIdInputRef = useRef(null);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page",  page);
    params.set("limit", LIMIT);
    if (userId.trim())  params.set("userId", userId.trim());
    if (pathQ.trim())   params.set("path",   pathQ.trim());
    if (method !== "ALL") params.set("method", method);
    const range = STATUS_RANGES[statusRange];
    if (range.min > 0)  params.set("statusMin", range.min);
    if (range.max < 999) params.set("statusMax", range.max);
    if (errorOnly)      params.set("errorOnly", "true");
    return params.toString();
  }, [page, userId, pathQ, method, statusRange, errorOnly]);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [logsRes, summaryRes] = await Promise.all([
        apiClient.get(`/mtrade/admin/audit-logs?${buildParams()}`),
        page === 1 ? apiClient.get("/mtrade/admin/audit-logs/summary") : Promise.resolve(null),
      ]);
      if (logsRes.data.status) {
        setLogs(logsRes.data.data);
        setTotal(logsRes.data.pagination?.total || 0);
      }
      if (summaryRes?.data?.status) {
        setSummary(summaryRes.data.data);
      }
    } catch {}
    finally { setLoading(false); }
  }, [buildParams, page]);

  // Initial load + filter changes reset to page 1
  useEffect(() => {
    setPage(1);
  }, [userId, pathQ, method, statusRange, errorOnly]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh every 15s when enabled
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchLogs(true), 15000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchLogs]);

  const totalPages = Math.ceil(total / LIMIT);

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  const errorCount   = logs.filter(l => l.statusCode >= 400).length;
  const avgDuration  = logs.length
    ? Math.round(logs.filter(l => l.durationMs).reduce((s, l) => s + l.durationMs, 0) / logs.filter(l => l.durationMs).length)
    : null;

  return (
    <div className="space-y-5">

      {/* Tab switcher */}
      <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-white/5 w-fit">
        {[
          { id: "requests", label: "Request Logs",      icon: "ri-file-list-3-line" },
          { id: "alerts",   label: "Transaction Alerts", icon: "ri-error-warning-line" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.id
                ? "bg-[#00C896]/15 text-[#00C896]"
                : "text-white/30 hover:text-white/50"
            }`}>
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "alerts" ? (
        <AlertsTab />
      ) : (<>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Audit Logs</h2>
          <p className="text-white/40 text-xs mt-0.5">
            Every authenticated request · {total.toLocaleString()} records
            {autoRefresh && <span className="ml-2 text-[#00C896]">· Live</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAutoRefresh(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
              autoRefresh
                ? "bg-[#00C896]/10 border-[#00C896]/30 text-[#00C896]"
                : "bg-[#161616] border-white/8 text-white/40 hover:text-white/60"
            }`}
          >
            <i className={`ri-pulse-line ${autoRefresh ? "animate-pulse" : ""}`} />
            {autoRefresh ? "Live" : "Auto-refresh"}
          </button>
          <button
            onClick={() => fetchLogs()}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#161616] border border-white/8 rounded-xl text-xs text-white/40 hover:text-white/60 transition-colors"
          >
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon="ri-file-list-3-line"
            label="Last 24h requests"
            value={summary.last24h?.toLocaleString()}
            sub="authenticated only"
            color="#00C896"
          />
          <StatCard
            icon="ri-error-warning-line"
            label="Errors (24h)"
            value={summary.errors24h?.toLocaleString()}
            sub={summary.last24h ? `${((summary.errors24h / summary.last24h) * 100).toFixed(1)}% error rate` : ""}
            color="#f87171"
          />
          <StatCard
            icon="ri-time-line"
            label="Avg response"
            value={summary.avgDurationMs ? `${summary.avgDurationMs}ms` : "—"}
            sub="last 24h"
            color="#fbbf24"
          />
          <StatCard
            icon="ri-user-line"
            label="Unique users (24h)"
            value={summary.uniqueUsers24h?.toLocaleString()}
            sub="distinct user IDs"
            color="#a78bfa"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#161616] border border-white/5 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* User ID search */}
          <div className="relative flex-1 min-w-48">
            <i className="ri-user-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input
              ref={userIdInputRef}
              value={userId}
              onChange={e => setUserId(e.target.value)}
              placeholder="Filter by User ID…"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 font-mono"
            />
          </div>

          {/* Path search */}
          <div className="relative flex-1 min-w-48">
            <i className="ri-route-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input
              value={pathQ}
              onChange={e => setPathQ(e.target.value)}
              placeholder="Filter by path… e.g. /bills/airtime"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 font-mono"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Method filter */}
          <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-white/5">
            {METHODS.map(m => (
              <button
                key={m}
                onClick={() => setMethod(m)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${
                  method === m
                    ? m === "ALL" ? "bg-white/10 text-white" : methodColor(m)
                    : "text-white/25 hover:text-white/40"
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          {/* Status range filter */}
          <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-white/5">
            {STATUS_RANGES.map((r, i) => {
              const sc = i === 0 ? { text: "text-white", bg: "bg-white/10" }
                       : i === 1 ? { text: "text-[#00C896]", bg: "bg-[#00C896]/10" }
                       : i === 2 ? { text: "text-amber-400", bg: "bg-amber-500/10" }
                       : { text: "text-red-400", bg: "bg-red-500/10" };
              return (
                <button
                  key={r.label}
                  onClick={() => setStatusRange(i)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${
                    statusRange === i ? `${sc.text} ${sc.bg}` : "text-white/25 hover:text-white/40"
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Error only */}
          <button
            onClick={() => setErrorOnly(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
              errorOnly
                ? "bg-red-500/10 border-red-500/20 text-red-400"
                : "bg-[#111] border-white/5 text-white/30 hover:text-white/50"
            }`}
          >
            <i className="ri-bug-line" />
            Errors only
          </button>

          {/* Clear filters */}
          {(userId || pathQ || method !== "ALL" || statusRange !== 0 || errorOnly) && (
            <button
              onClick={() => {
                setUserId(""); setPathQ("");
                setMethod("ALL"); setStatusRange(0);
                setErrorOnly(false);
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-white/30 hover:text-white/50 transition-colors"
            >
              <i className="ri-close-circle-line" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Log table */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">

        {/* Table header */}
        <div
          className="hidden md:grid px-4 py-2.5 border-b border-white/5 text-[9px] uppercase tracking-widest font-bold text-white/20"
          style={{ gridTemplateColumns: "6.5rem 3.5rem 1fr 4.5rem 3.5rem 5.5rem 1.5rem" }}
        >
          <span>Time</span>
          <span>Method</span>
          <span>Path</span>
          <span className="text-center">Status</span>
          <span className="text-right">Duration</span>
          <span className="text-right">IP</span>
          <span />
        </div>

        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="w-20 h-3 bg-white/5 rounded animate-pulse flex-shrink-0" />
                <div className="w-10 h-4 bg-white/5 rounded animate-pulse flex-shrink-0" />
                <div className="flex-1 h-3 bg-white/5 rounded animate-pulse" />
                <div className="w-10 h-4 bg-white/5 rounded animate-pulse flex-shrink-0" />
                <div className="w-8 h-3 bg-white/5 rounded animate-pulse flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <i className="ri-file-list-3-line text-white/10 text-4xl mb-2" />
            <p className="text-white/25 text-sm">No logs match your filters</p>
            <p className="text-white/15 text-xs mt-1">Try relaxing the filters or check a different user</p>
          </div>
        ) : (
          <div>
            {logs.map(log => (
              <LogRow
                key={log._id}
                log={log}
                expanded={expanded === log._id}
                onToggle={toggleExpand}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <p className="text-white/25 text-xs">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 disabled:opacity-25 transition-colors"
              >
                <i className="ri-arrow-left-s-line text-sm" />
              </button>
              {/* Page numbers — show a window around current page */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p     = start + i;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors ${
                      p === page
                        ? "bg-[#00C896]/15 text-[#00C896]"
                        : "bg-white/5 text-white/30 hover:bg-white/10"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 disabled:opacity-25 transition-colors"
              >
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* In-page error count note */}
      {!loading && errorCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/5 border border-red-500/10 rounded-xl">
          <i className="ri-error-warning-line text-red-400 text-sm" />
          <p className="text-red-400/80 text-xs">
            {errorCount} error{errorCount > 1 ? "s" : ""} on this page
            {avgDuration ? ` · avg response ${fmtDuration(avgDuration)}` : ""}
          </p>
        </div>
      )}
    </>)}
    </div>
  );
}
